import { Injectable, inject } from '@angular/core';
import { UserAccessRestrictions } from '@shared/interfaces/user-roles.model';
import { BehaviorSubject, combineLatest, map, Observable, take } from 'rxjs';

import { RestApiService } from './rest-api.service';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';

@Injectable({ providedIn: 'root' })
export class UserRolesAndPermissionsService {
  private api = inject(RestApiService);

  private loaded = false;
  private userRoles$ = new BehaviorSubject<UserAccessRestrictions>({
    Roles: [],
    Permissions: [],
  });

  getUserRolesAndPermissions(): Observable<UserAccessRestrictions> {
    const currentUserRolesVal = this.userRoles$.getValue();
    if (
      currentUserRolesVal.Roles.length &&
      currentUserRolesVal.Permissions.length
    ) {
      return this.userRoles$;
    } else {
      return this.api.getCustom<UserAccessRestrictions>('User/getroles').pipe(
        map(roles => {
          roles = Array.isArray(roles.Roles)
            ? roles
            : { Roles: [], Permissions: [] };
          this.userRoles$.next(roles);
          this.loaded = true;
          return roles;
        })
      );
    }
  }

  hasRoles(roles: RolesAndPermissions[]): Observable<boolean> {
    return this.getUserRolesAndPermissions().pipe(
      take(1),
      map(data => roles.every(role => data.Roles.includes(role)))
    );
  }

  hasPermissions(
    permissions: RolesAndPermissions[],
    isAndOperator = true
  ): Observable<boolean> {
    return this.getUserRolesAndPermissions().pipe(
      take(1),
      map(data => {
        return isAndOperator
          ? permissions.every(permission =>
              data.Permissions.includes(permission)
            )
          : permissions.some(permission =>
              data.Permissions.includes(permission)
            );
      })
    );
  }

  getPermissionsArray(): RolesAndPermissions[] | undefined {
    if (!this.loaded) {
      console.error(
        'Permissions are not loaded yet, please use one of the observable methods from UserRolesAndPermissionsService'
      );
      return;
    }

    return this.userRoles$.getValue().Permissions;
  }

  checkIfAccessControlRestrictionsChanged(): Observable<boolean> {
    return combineLatest([
      this.api.getCustom<UserAccessRestrictions>('User/getroles'),
      this.getUserRolesAndPermissions(),
    ]).pipe(
      take(1),
      map(([serverRolesPermissions, currentRolesPermissions]) => {
        return !(
          serverRolesPermissions.Permissions.every(ssPermission =>
            currentRolesPermissions.Permissions.includes(ssPermission)
          ) &&
          currentRolesPermissions.Permissions.every(currentPermission =>
            serverRolesPermissions.Permissions.includes(currentPermission)
          ) &&
          serverRolesPermissions.Roles.every(ssRole =>
            currentRolesPermissions.Roles.includes(ssRole)
          ) &&
          currentRolesPermissions.Roles.every(currentRole =>
            serverRolesPermissions.Roles.includes(currentRole)
          )
        );
      })
    );
  }
}
